import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// ========================= Types =========================

export type PaymentStatus =
  | "PENDING"
  | "PROCESSING"
  | "CREDIT_APPROVED"
  | "PAID"
  | "PARTIAL"
  | "FAILED"
  | "REFUNDED"
  | "PARTIAL_REFUND"
  | "VOIDED";
export type PaymentMethodType =
  | "SAVED_CARD"
  | "PAYMENT_LINK"
  | "CREDIT"
  | "INVOICE"
  | "CASH"
  | "E_TRANSFER"
  | "WECHAT";

export interface ProcessPaymentRequest {
  paymentMethodId?: string; // Required for CARD payments
}

export interface ProcessPaymentResponse {
  success: boolean;
  paymentStatus: PaymentStatus;
  paymentIntentId?: string;
  invoiceId?: string;
  invoiceUrl?: string;
  dueDate?: string;
  awaitingManualPayment?: boolean;
  paymentType?: string;
  amountCents?: number;
}

export interface RecordManualPaymentResponse {
  success: boolean;
  order: {
    id: string;
    orderNumber: number;
    paymentStatus: PaymentStatus;
    paidCents: number;
    paymentMetadata: Record<string, unknown>;
    paymentMethodType?: PaymentMethodType;
  };
  invoice?: {
    id: string;
    url: string;
    pdf: string;
    paymentInstructions: string;
  };
}

export interface PricingBreakdown {
  baseRateCents: number;
  weightSurchargeCents: number;
  deliverySpeedCents: number;
  insuranceCents: number;
  propertyTypeSurchargeCents: number;
  /**
   * ⚠️ `carrierCostCents` and `platformFeeCents` are GONE — what the carrier is
   * paid and what EZX takes. The server stopped sending them (they were a margin
   * leak on a route a customer can reach), and the columns behind them are dropped:
   * settlement is rebuilt on `FinancialEvent`, not on a mutable copy of the split.
   *
   * Declaring them here after the server stopped sending them is worse than useless
   * — the compiler would happily agree that `breakdown.platformFeeCents` is a
   * `number` right up until it rendered `undefined`.
   */
  pickupFeeCents: number;
  deliveryFeeCents: number;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
}

export interface NetTermsInfo {
  paymentTerms: {
    id: string;
    name: string;
    code: string;
    paymentDueDays: number;
    dayInterval: number;
  };
  pendingOrderCount: number;
  pendingAmountCents: number;
  daysUntilInvoice: number;
  estimatedInvoiceDate: string;
}

export interface PaymentStatusResponse {
  paymentStatus: PaymentStatus;
  paymentMethodType?: PaymentMethodType;
  totalCents?: number;
  paidAmountCents: number;
  originallyPaidAmountCents?: number;
  remainingCents: number;
  refundedAmountCents?: number;
  paymentMetadata?: Record<string, unknown>;
  pricingBreakdown?: PricingBreakdown | null;
  paymentDueDate?: string | null;
  netTermsInfo?: NetTermsInfo | null;
  invoice?: {
    id: string;
    stripeInvoiceId?: string;
    status: string;
    number?: string;
    createdAt: string;
    dueDate?: string;
    amountDue?: number;
    amountPaid?: number;
    hostedUrl?: string;
    pdfUrl?: string;
    orderDetails?: {
      orderCount: number;
      orderNumbers: number[];
      totalCents: number;
      dateRange?: {
        start: string;
        end: string;
      };
    };
  };
}

export interface CancelOrderWithRefundResponse {
  success: boolean;
  order: {
    id: string;
    orderNumber: number;
    paymentStatus: PaymentStatus;
  };
  refund?: {
    method: string;
    amountCents?: number;
    stripeRefundId?: string;
    transactionId?: string;
    status?: string;
    action?: string;
    invoiceId?: string;
    notes?: string;
  };
}

export interface CustomerCreditBalance {
  balanceCents: number;
  currency: string;
  lastSyncedAt: string;
  cached: boolean;
}

export interface CustomerCreditTransaction {
  id: string;
  type: "credit" | "debit";
  amountCents: number;
  description?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export interface CustomerCreditTransactionsResponse {
  transactions: CustomerCreditTransaction[];
  hasMore: boolean;
}

export interface DepositCreditRequest {
  amountCents: number;
  description?: string;
}

export interface DepositCreditResponse {
  success: boolean;
  transactionId: string;
  amountCents: number;
  newBalanceCents: number;
  currency: string;
}

export interface UseCreditRequest {
  amountCents: number;
  description: string;
  orderId?: string;
}

export interface UseCreditResponse {
  success: boolean;
  paymentIntentId: string;
  amountCents: number;
  newBalanceCents: number;
  currency: string;
}

export type ReceiptType = "PAYMENT" | "REFUND";

export interface Receipt {
  id: string;
  receiptNumber: string;
  type: ReceiptType;
  paymentId: string;
  orderId: string;
  fileUrl: string;
  emailedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type PaymentTransactionType = "DEBIT" | "CREDIT";

export interface Payment {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: PaymentStatus;
  method: PaymentMethodType;
  amountCents: number;
  transactionType: PaymentTransactionType;
  parentPaymentId?: string;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  stripeRefundId?: string;
  stripeReceiptUrl?: string;
  failureReason?: string;
  processedBy?: string;
  referenceNumber?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  receipts?: Receipt[];
  order?: {
    id: string;
    orderNumber: number;
    placerCustomer?: {
      user?: {
        firstName: string;
        lastName: string;
        email: string;
      };
    };
  };
}

export interface GetPaymentsParams {
  limit?: number;
  offset?: number;
  status?: PaymentStatus;
  method?: PaymentMethodType;
  customerId?: string;
  orderId?: string;
}

export interface PaymentInstruction {
  id: string;
  method: PaymentMethodType;
  displayName: string;
  instructions: string;
  additionalInfo?: string;
  eTransferEmail?: string;
  wechatQRCode?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePaymentInstructionRequest {
  method: PaymentMethodType;
  displayName: string;
  instructions: string;
  additionalInfo?: string;
  isActive?: boolean;
}

export interface UpdatePaymentInstructionRequest {
  displayName?: string;
  instructions?: string;
  additionalInfo?: string;
  isActive?: boolean;
}

export type PriceAdjustmentTypeServer =
  | "ADDRESS_CHANGE"
  | "WEIGHT_ADJUSTMENT"
  | "SERVICE_UPGRADE"
  | "DISCOUNT"
  | "FEE"
  | "TAX_ADJUSTMENT"
  | "OTHER";

export interface PriceAdjustment {
  id: string;
  orderId: string;
  type: PriceAdjustmentTypeServer;
  amountCents: number;
  reason?: string;
  appliedBy?: string;
  createdAt: string;
  updatedAt: string;
}

// Row shape returned by GET /api/v2/admin/pricing/adjustments (Phase 16b).
// Has shipmentId/pickupBookingId scoping fields the legacy per-order shape
// lacks, plus a joined shipmentNumber so the UI can render "SHP-126" without
// a second roundtrip.
export interface PriceAdjustmentListItem {
  id: string;
  orderId: string | null;
  shipmentId: string | null;
  pickupBookingId: string | null;
  type: PriceAdjustmentTypeServer;
  amountCents: number;
  reason: string | null;
  appliedBy: string | null;
  createdAt: string;
  shipment: { shipmentNumber: string } | null;
}

export interface ListPriceAdjustmentsParams {
  orderId?: string;
  shipmentId?: string;
  pickupBookingId?: string;
  type?: PriceAdjustmentTypeServer;
  from?: string;
  to?: string;
  limit?: number;
}

// ========================= Order Payment Processing =========================

export function useProcessOrderPayment(
  orderId: string,
  options?: {
    onSuccess?: (data: ProcessPaymentResponse) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (
      request: ProcessPaymentRequest
    ): Promise<ProcessPaymentResponse> => {
      const { data } = await axios.post<ProcessPaymentResponse>(
        `${API_URL}/api/v2/orders/${orderId}/process-payment`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export function useOrderPaymentStatus(orderId: string) {
  const { token } = useSession();

  return useQuery<PaymentStatusResponse>({
    queryKey: ["order-payment-status", orderId],
    queryFn: async () => {
      const { data } = await axios.get<PaymentStatusResponse>(
        `${API_URL}/api/v2/orders/${orderId}/payment-status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    enabled: !!orderId && !!token,
  });
}

// ========================= Order Refund =========================
// Note: order-level cancel (useCancelOrder / CancelOrderDialog) was removed —
// it had no rendered UI. Cancellation is shipment-scoped (request-cancellation
// + admin approve). The /api/v2/orders/:orderId/cancel endpoint is orphaned.

// ========================= Customer Credit Management =========================

export function useGetPayments(params: GetPaymentsParams = {}) {
  const { token } = useSession();

  return useQuery<Payment[]>({
    queryKey: ["payments", params],
    queryFn: async () => {
      const { data } = await axios.get<Payment[]>(
        `${API_URL}/api/v2/payments`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// ========================= Payment Instructions =========================

export function useGetPaymentInstructions() {
  const { token } = useSession();

  return useQuery<PaymentInstruction[]>({
    queryKey: ["payment-instructions"],
    queryFn: async () => {
      const { data } = await axios.get<{
        success: boolean;
        data: PaymentInstruction[];
      }>(`${API_URL}/api/v2/payment-instructions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data.data;
    },
    enabled: !!token,
  });
}

export function useCreatePaymentInstruction(options?: {
  onSuccess?: (data: PaymentInstruction) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (
      request: CreatePaymentInstructionRequest
    ): Promise<PaymentInstruction> => {
      const { data } = await axios.post<PaymentInstruction>(
        `${API_URL}/api/v2/payment-instructions`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export function useUpdatePaymentInstruction(options?: {
  onSuccess?: (data: PaymentInstruction) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (
      request: UpdatePaymentInstructionRequest & { id: string }
    ): Promise<PaymentInstruction> => {
      const { id, ...updateData } = request;
      const { data } = await axios.put<PaymentInstruction>(
        `${API_URL}/api/v2/payment-instructions/${id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export function useDeletePaymentInstruction(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await axios.delete(`${API_URL}/api/v2/payment-instructions/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// ========================= Payment Processing =========================

export function useProcessPayment(
  orderId: string,
  options?: {
    onSuccess?: (data: ProcessPaymentResponse) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (request: {
      amountCents: number;
      method: PaymentMethodType;
    }): Promise<ProcessPaymentResponse> => {
      const { data } = await axios.post<ProcessPaymentResponse>(
        `${API_URL}/api/v2/orders/${orderId}/process-payment`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export function useGetPaymentLink(orderId: string) {
  const { token } = useSession();

  return useQuery<string | null>({
    queryKey: ["payment-link", orderId],
    queryFn: async () => {
      const { data } = await axios.get<{
        data?: { paymentLink?: string };
      }>(`${API_URL}/api/v2/orders/${orderId}/payments/link`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data?.data?.paymentLink ?? null;
    },
    enabled: !!orderId && !!token,
  });
}

export function useGeneratePaymentLink(
  orderId: string,
  options?: {
    onSuccess?: (link: string) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();

  return useMutation<string, Error, boolean | undefined>({
    mutationFn: async (forceNew = false): Promise<string> => {
      const { data } = await axios.post<{
        success: boolean;
        data?: { paymentLink?: string };
      }>(
        `${API_URL}/api/v2/orders/${orderId}/payments/link`,
        { forceNew },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // The backend returns: { success: true, data: { paymentLink: "..." } }
      const link = data?.data?.paymentLink;

      if (!link) {
        throw new Error("Failed to generate payment link");
      }

      return link;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export function useRecordManualPaymentV2(
  orderId: string,
  options?: {
    onSuccess?: (data: RecordManualPaymentResponse) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (request: {
      method: PaymentMethodType;
      amountCents: number;
      reference?: string;
      referenceNumber?: string;
      notes?: string;
    }): Promise<RecordManualPaymentResponse> => {
      const { data } = await axios.post<RecordManualPaymentResponse>(
        `${API_URL}/api/v2/orders/${orderId}/payments/record-manual`,
        {
          method: request.method,
          amountCents: request.amountCents,
          reference: request.reference ?? request.referenceNumber,
          notes: request.notes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export function useOrderPayments(orderId: string) {
  const { token } = useSession();

  return useQuery<Payment[]>({
    queryKey: ["order-payments", orderId],
    queryFn: async () => {
      const { data } = await axios.get<{ success: boolean; data: Payment[] }>(
        `${API_URL}/api/v2/orders/${orderId}/payments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.data;
    },
    enabled: !!orderId && !!token,
  });
}

// ========================= Order Adjustments =========================

export function useOrderAdjustments(orderId: string) {
  const { token } = useSession();

  return useQuery<PriceAdjustment[]>({
    queryKey: ["orderAdjustments", orderId],
    queryFn: async () => {
      const { data } = await axios.get<{ adjustments: PriceAdjustment[] }>(
        `${API_URL}/api/v2/orders/${orderId}/adjustments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.adjustments || [];
    },
    enabled: !!orderId && !!token,
  });
}

// Alias for backwards compatibility
export const usePaymentInstructions = useGetPaymentInstructions;

// Phase 16b: cross-scope list of PriceAdjustment rows. All filters AND-combined.
export function useListPriceAdjustments(params: ListPriceAdjustmentsParams) {
  const { token } = useSession();

  return useQuery<PriceAdjustmentListItem[]>({
    queryKey: ["price-adjustments", params],
    queryFn: async () => {
      const search = new URLSearchParams();
      if (params.orderId) search.set("orderId", params.orderId);
      if (params.shipmentId) search.set("shipmentId", params.shipmentId);
      if (params.pickupBookingId)
        search.set("pickupBookingId", params.pickupBookingId);
      if (params.type) search.set("type", params.type);
      if (params.from) search.set("from", params.from);
      if (params.to) search.set("to", params.to);
      if (params.limit) search.set("limit", String(params.limit));

      const { data } = await axios.get<{ data: PriceAdjustmentListItem[] }>(
        `${API_URL}/api/v2/admin/pricing/adjustments?${search.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

// ========================= NET Terms Payment APIs =========================

export interface PaymentTerms {
  id: string;
  name: string;
  code: string;
  paymentDueDays: number;
  dayInterval: number;
  creditLimit?: number;
  description?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerPaymentTermsInfo {
  paymentTerms: PaymentTerms[];
  creditLimit: number;
  outstandingBalance: number;
  availableCredit: number;
  lastInvoiceDate: string | null;
  pendingInvoiceOrders: number;
  unpaidInvoiceCount: number;
  hasUnpaidInvoices: boolean;
}

export function useGetPaymentTerms() {
  const { token } = useSession();

  return useQuery<PaymentTerms[]>({
    queryKey: ["payment-terms"],
    queryFn: async () => {
      const { data } = await axios.get<{ data: PaymentTerms[] }>(
        `${API_URL}/api/v2/payment-terms`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

interface GenerateInvoiceResponse {
  success: boolean;
  invoiceId?: string;
  message?: string;
}

export function useGenerateManualNetTermsInvoice(options?: {
  onSuccess?: (data: GenerateInvoiceResponse) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      customerId,
      paymentTermsId,
    }: {
      customerId: string;
      paymentTermsId: string;
    }): Promise<GenerateInvoiceResponse> => {
      // UUID generated once per click. Retry of the same logical action
      // reuses it so Stripe dedups; a deliberate re-click gets a fresh UUID.
      const idempotencyKey = crypto.randomUUID();
      const { data } = await axios.post<GenerateInvoiceResponse>(
        `${API_URL}/api/v2/customers/${customerId}/generate-invoice`,
        { paymentTermsId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Idempotency-Key": idempotencyKey,
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

interface PendingNetTermsOrdersResponse {
  orders: {
    id: string;
    orderNumber: number;
    totalCents: number;
    createdAt: string;
  }[];
  totalPendingCents: number;
}

export function useGetPendingNetTermsOrders(customerId: string) {
  const { token } = useSession();

  return useQuery<PendingNetTermsOrdersResponse>({
    queryKey: ["pending-net-terms-orders", customerId],
    queryFn: async () => {
      const { data } = await axios.get<PendingNetTermsOrdersResponse>(
        `${API_URL}/api/v2/customers/${customerId}/pending-net-terms-orders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    enabled: !!customerId && !!token,
  });
}

export function useGetCustomerPaymentTerms(customerId: string) {
  const { token } = useSession();

  return useQuery<CustomerPaymentTermsInfo>({
    queryKey: ["customer-payment-terms", customerId],
    queryFn: async () => {
      const { data } = await axios.get<{ data: CustomerPaymentTermsInfo }>(
        `${API_URL}/api/v2/customers/${customerId}/payment-terms`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.data;
    },
    enabled: !!customerId && !!token,
  });
}

interface NetTermsPaymentResponse {
  success: boolean;
  paymentStatus: PaymentStatus;
}

export function useRecordNetTermsPayment(
  orderId: string,
  options?: {
    onSuccess?: (data: NetTermsPaymentResponse) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (
      paymentTermsId: string
    ): Promise<NetTermsPaymentResponse> => {
      const { data } = await axios.post<{ data: NetTermsPaymentResponse }>(
        `${API_URL}/api/v2/orders/${orderId}/net-terms-payment`,
        { paymentTermsId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// ========================= Payment Terms =========================

export function usePaymentTerms() {
  const { token } = useSession();

  return useQuery({
    queryKey: ["payment-terms"],
    queryFn: async () => {
      const { data } = await axios.get<{ data: PaymentTerms[] }>(
        `${API_URL}/api/v2/payment-terms`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useCreatePaymentTerm() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      code: string;
      paymentDueDays: number;
      creditLimit?: number | null;
      dayInterval: number;
      description?: string;
      sortOrder?: number;
    }) => {
      const response = await axios.post<{ data: PaymentTerms }>(
        `${API_URL}/api/v2/payment-terms`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["payment-terms"] });
    },
  });
}

export function useUpdatePaymentTerm() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      code?: string;
      paymentDueDays?: number;
      creditLimit?: number | null;
      dayInterval?: number;
      description?: string;
      sortOrder?: number;
      isActive?: boolean;
    }) => {
      const response = await axios.put<{ data: PaymentTerms }>(
        `${API_URL}/api/v2/payment-terms/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["payment-terms"] });
    },
  });
}

export function useDeletePaymentTerm() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_URL}/api/v2/payment-terms/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["payment-terms"] });
    },
  });
}
